import {
  Group,
  Menu,
  ActionIcon,
  Image,
  Stack,
  Modal,
  Button,
  Tooltip,
  Badge,
  Text,
  Flex,
  Popover,
  Box,
  Radio,
  UseTreeReturnType,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import {
  IconArrowsSort,
  IconEye,
  IconLink,
  IconZoomOut,
  IconMenu2,
  IconFilePencil,
  IconTrash,
} from "@tabler/icons-react"
import _ from "lodash"

import {
  DisplayOptions,
  Position,
  DigestType,
  DigestResponses,
} from "@costory/types/endpoints/digest"

import { ICON_SIZE, ICON_STROKE } from "@costory/shared/const"
import { getdeterministicNodeId } from "@costory/shared/utils/digest"
import { formatNumber } from "@costory/shared/utils/format"

import { VdimHelper } from "@costory/front/pages/DigestV2/VdimHelper"
import { ButtonAddTo } from "@costory/front/pages/DigestV2/components/ButtonAddToDigest"
import {
  NodeTree,
  getQuery,
  deleteChild,
  addChild,
  deleteAllChilds,
  replaceDigestItem,
  unhideItem,
  getPercentageChange,
} from "@costory/front/pages/DigestV2/helper"
import { useAxesMapperQuery } from "@costory/front/queries/axes"

export function NodeRendering({
  node,
  isDiffMode,
  hasChildren,
  exploreInDrawer,
  level,
  form,
  checkedNodes,
  setCheckedNodes,
  tree,
  setManuallyCollapsedRows,
  costColumn,
  digest,
  minDiffToHide,
  minRelativeDiffToHide,
}: {
  node: NodeTree
  isDiffMode: boolean
  hasChildren: boolean
  expanded: boolean
  exploreInDrawer: () => void
  level: number
  color: string
  form: UseFormReturnType<DigestType>
  checkedNodes: Map<string, boolean>
  setCheckedNodes: React.Dispatch<React.SetStateAction<Map<string, boolean>>>
  tree: UseTreeReturnType
  setManuallyCollapsedRows: React.Dispatch<React.SetStateAction<string[]>>
  costColumn: string
  digest: DigestResponses.Investigation
  minDiffToHide: number
  minRelativeDiffToHide: number
}) {
  const {
    id,
    displayOptions: { isOthersAtBottom, orderByColumn, order },
  } = node.nodeProps
  const queryMapper = useAxesMapperQuery()

  const value = node.isOthers
    ? ""
    : node.nodeProps.position.filter(
        (el: Position[0]) => el.key === node.nodeProps.direction,
      )[0]?.value || ""

  const labelText = `${
    queryMapper?.data?.get(node.nodeProps.direction) || node.nodeProps.direction
  } ${value}`

  const changeOrder = (
    orderByColumn: DisplayOptions["orderByColumn"],
    order: DisplayOptions["order"],
    isOthersAtBottom: boolean,
  ) => {
    replaceDigestItem(
      form,
      {
        displayOptions: {
          displayContext: true,
          orderByColumn,
          order,
          isOthersAtBottom,
        },
      },
      id,
    )
  }
  const [openedZoomImage, handlersZoomImage] = useDisclosure()
  const checkedNodesIds = _.keys(_.pickBy(Object.fromEntries(checkedNodes)))

  const handleUnhideItem = (id: string) => {
    if (!node.nodeProps.hiddenItems) return
    unhideItem(node.nodeProps.id, id, form)
  }

  const handleHideItem = (node: NodeTree) => {
    const digestMap = new Map(
      digest.map((d) => [getdeterministicNodeId(d.arr), d]),
    )
    deleteChild(node.nodeProps.id, form, digestMap, costColumn)
  }

  const renderHiddenItemsSummary = () => {
    const hasHiddenItems =
      node.nodeProps.hiddenItems && node.nodeProps.hiddenItems.length > 0

    const hiddenItems = node.nodeProps.hiddenItems || []

    const isZeroCostOthers =
      node.nodeProps.direction === "Others" &&
      node.data.costCurrent === 0 &&
      node.nodeProps.parentId !== null

    if (hasHiddenItems || isZeroCostOthers) {
      const count = hasHiddenItems ? hiddenItems.length : 0

      if (hasHiddenItems && count > 0) {
        const totalCurrent = hiddenItems.reduce(
          (sum, item) => sum + (item?.costCurrent || 0),
          0,
        )
        const totalPrevious = hiddenItems.reduce(
          (sum, item) => sum + (item.costPrevious || 0),
          0,
        )
        const totalChangePercentage = getPercentageChange(
          totalCurrent,
          totalPrevious,
        )
        const formattedTotalChange = `${totalChangePercentage > 0 ? "+" : ""}${totalChangePercentage.toFixed(1)}%`
        const totalChangeColor =
          totalChangePercentage > 0
            ? "costIncrease"
            : totalChangePercentage < 0
              ? "costDecrease"
              : "textMuted"

        return (
          <Popover position="right" shadow="md" width={350}>
            <Popover.Target>
              <Badge
                color="indigo"
                size="sm"
                variant="filled"
                style={{ cursor: "pointer" }}
                data-intercom-target="costory-tour-digest-node-hidden-items"
              >
                {count} HIDDEN ITEMS
              </Badge>
            </Popover.Target>
            <Popover.Dropdown style={{ maxHeight: 450, overflow: "auto" }}>
              <Stack>
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>Hidden Items</Text>
                  <Group gap="xs">
                    <Text fw={500}>
                      {formatNumber({ value: totalCurrent - totalPrevious })}
                    </Text>
                    <Text c={totalChangeColor} size="sm" fw={500}>
                      ({formattedTotalChange})
                    </Text>
                  </Group>
                </Group>

                {_(hiddenItems)
                  .sortBy((item) =>
                    Math.abs(item.costCurrent - item.costPrevious),
                  )
                  .reverse()
                  .value()
                  .map((item, index) => {
                    const itemCurrent = item.costCurrent || 0
                    const itemPrevious = item.costPrevious || 0
                    const itemChangePercentage = getPercentageChange(
                      itemCurrent,
                      itemPrevious,
                    )
                    const formattedItemChange = `${itemChangePercentage > 0 ? "+" : ""}${itemChangePercentage.toFixed(1)}%`
                    const itemChangeColor =
                      itemChangePercentage > 0
                        ? "costIncrease"
                        : itemChangePercentage < 0
                          ? "costDecrease"
                          : "textMuted"

                    return (
                      <Box key={index} mb="xs">
                        <Group
                          justify="space-between"
                          style={{
                            overflowWrap: "break-word",
                            wordBreak: "break-all",
                            flex: 1,
                          }}
                        >
                          <Text
                            size="sm"
                            fw={500}
                            style={{ overflowWrap: "break-word", flex: 1 }}
                          >
                            {item.position.filter(
                              (el: Position[0]) => el.key === item.direction,
                            )[0]?.value || ""}
                          </Text>
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => handleUnhideItem(item.id)}
                            leftSection={
                              <IconEye size={ICON_SIZE} stroke={ICON_STROKE} />
                            }
                          >
                            Unhide
                          </Button>
                        </Group>
                        <Group justify="space-between" mt={2}>
                          <Text size="xs" c="dimmed">
                            Current: {formatNumber({ value: itemCurrent })}
                          </Text>
                          <Text size="xs" c="dimmed">
                            Previous: {formatNumber({ value: itemPrevious })}
                          </Text>
                          <Text size="xs" c={itemChangeColor} fw={500}>
                            ({formattedItemChange})
                          </Text>
                        </Group>
                      </Box>
                    )
                  })}
              </Stack>
            </Popover.Dropdown>
          </Popover>
        )
      }
    }

    return null
  }

  return (
    <>
      <ModalZoomImage
        node={node}
        opened={openedZoomImage}
        close={handlersZoomImage.close}
      />
      <Stack>
        <Group gap={5} justify="space-between">
          <Flex w="100%" gap={10}>
            <Group gap="xs">
              <NodeText
                node={node}
                isDiffMode={isDiffMode}
                exploreInDrawer={exploreInDrawer}
                labelText={labelText}
              />
              {renderHiddenItemsSummary()}
            </Group>
            {!node.isOthers && (
              <ButtonAddTo
                costColumn={costColumn}
                digest={digest}
                addChild={(direction) => {
                  deleteAllChilds(id, form)
                  addChild(
                    form,
                    direction,
                    digest,
                    id,
                    () => tree.expand(node.value),
                    costColumn,
                    minDiffToHide,
                    minRelativeDiffToHide,
                  )
                  setManuallyCollapsedRows((prev) =>
                    prev.filter((row) => row !== node.value),
                  )
                }}
                node={node}
                disabled={node.isOthers}
              />
            )}
            {(() => {
              const canShowCollapseButton =
                node.nodeProps.parentId !== null &&
                node.nodeProps.direction !== "Total Cloud Costs" &&
                level > 1 &&
                !node.isOthers

              return (
                canShowCollapseButton && (
                  <Tooltip label="Collapse into Others">
                    <ActionIcon
                      onClick={() => handleHideItem(node)}
                      color="textMuted"
                      data-intercom-target="costory-tour-digest-node-zoom-out"
                    >
                      <IconZoomOut size={ICON_SIZE} stroke={ICON_STROKE} />
                    </ActionIcon>
                  </Tooltip>
                )
              )
            })()}
            {checkedNodesIds.includes(id) && (
              <VdimHelper
                checkedUuid={checkedNodesIds}
                form={form}
                cleanSelected={() =>
                  setCheckedNodes(new Map<string, boolean>())
                }
              />
            )}
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <Tooltip label="Menu">
                  <ActionIcon
                    variant="transparent"
                    aria-label="Menu"
                    color="gray"
                    data-intercom-target="costory-tour-digest-node-menu"
                  >
                    <IconMenu2 size={ICON_SIZE} stroke={ICON_STROKE} />
                  </ActionIcon>
                </Tooltip>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Contextualize</Menu.Label>
                <Menu.Item
                  leftSection={
                    <IconFilePencil size={ICON_SIZE} stroke={ICON_STROKE} />
                  }
                  onClick={exploreInDrawer}
                  data-intercom-target="costory-tour-digest-node-edit-context-1"
                >
                  Edit Context
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    <IconLink size={ICON_SIZE} stroke={ICON_STROKE} />
                  }
                  onClick={() => {
                    const { queryFormatted } = getQuery(
                      node,
                      form.values.period,
                    )
                    if (queryFormatted) {
                      const baseUrl = window.location.origin
                      const url = `${baseUrl}/explorer?${queryFormatted}`
                      window.open(url, "_blank")
                    }
                  }}
                  disabled={
                    !node.nodeProps.parentId ||
                    node.isOthers ||
                    !node.nodeProps.position?.length
                  }
                  data-intercom-target="costory-tour-digest-node-investigate"
                >
                  Investigate
                </Menu.Item>
                <Menu.Divider />
                <Menu.Label>Organize</Menu.Label>
                <Menu.Item
                  leftSection={
                    <IconTrash size={ICON_SIZE} stroke={ICON_STROKE} />
                  }
                  onClick={() => deleteAllChilds(id, form)}
                  disabled={!hasChildren}
                  data-intercom-target="costory-tour-digest-node-delete-children"
                >
                  Delete All Childs
                </Menu.Item>
                <Menu.Sub>
                  <Menu.Sub.Target>
                    <Menu.Sub.Item
                      disabled={!hasChildren}
                      leftSection={
                        <IconArrowsSort size={ICON_SIZE} stroke={ICON_STROKE} />
                      }
                      data-intercom-target="costory-tour-digest-node-change-order"
                    >
                      Change Ordering
                    </Menu.Sub.Item>
                  </Menu.Sub.Target>

                  <Menu.Sub.Dropdown>
                    <Menu.Item
                      onClick={() => {
                        if (orderByColumn !== "absChange")
                          changeOrder("absChange", "desc", isOthersAtBottom)
                      }}
                    >
                      <Flex align="center" gap="xs">
                        <Radio
                          size="xs"
                          checked={orderByColumn === "absChange"}
                          styles={{
                            root: { transform: "scale(0.8)" },
                          }}
                          readOnly
                        />
                        <Text fz="sm">by Absolute Diff Desc</Text>
                      </Flex>
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => {
                        if (orderByColumn !== "cost")
                          changeOrder("cost", "desc", isOthersAtBottom)
                      }}
                    >
                      <Flex align="center" gap="xs">
                        <Radio
                          size="xs"
                          checked={orderByColumn === "cost"}
                          styles={{
                            root: { transform: "scale(0.8)" },
                          }}
                          readOnly
                        />
                        <Text fz="sm">by Absolute Cost Desc</Text>
                      </Flex>
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      onClick={() => {
                        changeOrder(orderByColumn, order, !isOthersAtBottom)
                      }}
                    >
                      <Flex align="center" gap="xs">
                        <Radio
                          size="xs"
                          checked={isOthersAtBottom}
                          styles={{
                            root: { transform: "scale(0.8)" },
                          }}
                          readOnly
                        />
                        <Text fz="sm">Keep Others at bottom</Text>
                      </Flex>
                    </Menu.Item>
                  </Menu.Sub.Dropdown>
                </Menu.Sub>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Group>
      </Stack>
    </>
  )
}

function NodeText({
  exploreInDrawer,
  labelText,
}: {
  node: NodeTree
  isDiffMode: boolean
  exploreInDrawer: () => void
  labelText: string
}) {
  return (
    <Tooltip label="See in Explorer Modal">
      <Text
        onClick={exploreInDrawer}
        size="sm"
        fw={500}
        maw={320}
        style={{
          cursor: "pointer",
          overflowWrap: "break-word",
          wordBreak: "break-all",
          flex: 1,
        }}
        data-intercom-target="costory-tour-digest-node-edit-context-2"
      >
        {labelText}
      </Text>
    </Tooltip>
  )
}

function ModalZoomImage({
  node,
  opened,
  close,
}: {
  node: NodeTree
  opened: boolean
  close: () => void
}) {
  return (
    <>
      <Modal
        size="90%"
        opened={opened}
        onClose={close}
        title="Zoom"
        styles={{
          body: { backgroundColor: "var(--mantine-color-surfaceGround-0)" },
        }}
      >
        <Group justify="center">
          <Image w={800} src={node.nodeProps.imgUrl} radius={2} />
        </Group>
      </Modal>
    </>
  )
}
